<script>
import {
  batchUpdateIn,
  deepCopyComponent,
  findComponent,
  findComponentPathById,
  generateId,
  updateTreeIn
} from '@/placeholders/utils'
import { registerKeyboardAction } from '@/placeholders/vseditor/plugins/keyboard'

import PluginGridVue from '@/placeholders/vseditor/plugins/plugin-grid.vue'
import PluginSelectionVue from '@/placeholders/vseditor/plugins/plugin-selection.vue'
import EditorViewVue from '@/placeholders/vseditor/editor-view.vue'
import FooterVue from '@/placeholders/vseditor/footer.vue'

import {
  EVENT_APPLICATION_CLEAR,
  EVENT_APPLICATION_REDO,
  EVENT_APPLICATION_UNDO,
  EVENT_COMPONENT_ADD,
  EVENT_COMPONENT_DELETE,
  EVENT_COMPONENT_DUPLICATE,
  EVENT_COMPONENT_SELECT,
  EVENT_COMPONENT_TRANSFORM,
  EVENT_COMPONENT_UNSELECT
} from '@/placeholders/vseditor/event-enums'

// 歷史紀錄和指標
let historyStack = [[]]
let historyPointer = 0

/**
 * PDFCanvas 組件
 *
 * 覆蓋在 PDF 上的畫布層，用於設計與管理控件 (如輸入框、勾選框等)
 */
export default {
  name: 'PDFCanvas',
  props: {
    initialPDFControls: {
      type: Array,
      default: () => []
    },
    pdfWidth: { type: Number, default: 0 },
    pdfHeight: { type: Number, default: 0 }
  },
  data() {
    return {
      controls: [],
      currentId: '',
      currentPath: [],
      controlled: {}
    }
  },
  created() {
    this.setControls(this.initialPDFControls)
    this.clearCurrentComponent()

    // 註冊 Event-Bus 事件監聽
    this.eventbus.$on(EVENT_COMPONENT_ADD, this.addControl)
    this.eventbus.$on(EVENT_COMPONENT_SELECT, this.handleSelect)
    this.eventbus.$on(EVENT_COMPONENT_TRANSFORM, this.handleTransform)
    this.eventbus.$on(EVENT_COMPONENT_UNSELECT, this.handleUnselect)
    this.eventbus.$on(EVENT_COMPONENT_DUPLICATE, this.duplicateComponent)
    this.eventbus.$on(EVENT_COMPONENT_DELETE, this.deleteComponent)
    this.eventbus.$on(EVENT_APPLICATION_REDO, this.handleRedo)
    this.eventbus.$on(EVENT_APPLICATION_UNDO, this.handleUndo)
    this.eventbus.$on(EVENT_APPLICATION_CLEAR, this.handleClear)

    registerKeyboardAction(this)
  },
  beforeDestroy() {
    // 移除 Event-Bus 事件監聽(這很重要！ 避免記憶體泄露與事件重複觸發)
    this.eventbus.$off(EVENT_COMPONENT_ADD, this.addControl)
    this.eventbus.$off(EVENT_COMPONENT_SELECT, this.handleSelect)
    this.eventbus.$off(EVENT_COMPONENT_TRANSFORM, this.handleTransform)
    this.eventbus.$off(EVENT_COMPONENT_UNSELECT, this.handleUnselect)
    this.eventbus.$off(EVENT_COMPONENT_DUPLICATE, this.duplicateComponent)
    this.eventbus.$off(EVENT_COMPONENT_DELETE, this.deleteComponent)
    this.eventbus.$off(EVENT_APPLICATION_REDO, this.handleRedo)
    this.eventbus.$off(EVENT_APPLICATION_UNDO, this.handleUndo)
    this.eventbus.$off(EVENT_APPLICATION_CLEAR, this.handleClear)
  },
  methods: {
    /**
     * 將輸入的元件資料轉換為標準格式
     * @param {Array} components 元件資料陣列
     * @param {string} parentId 父元件ID
     * @returns {Array} 轉換後的元件陣列
     */
    getComponents(components, parentId) {
      // 縮放時需要鎖定長寬比例的類型
      const lockedRatioTypes = ['signature']

      return components.map((item) => ({
        type: item.type,
        children: item.type === 'container' ? [] : void 0,
        id: generateId(),
        transform: {
          x: item.x - (item.x % 10),
          y: item.y - (item.y % 10),
          width: item.width,
          height: item.height,
          rotation: 0
        },
        minHeight: 10,
        minWidth: 10,
        rotatable: true,
        resizable: true,
        draggable: true,
        acceptRatio: lockedRatioTypes.includes(item.type),
        zoom: 1,
        active: false,
        parent: true,
        parentId,
        resizeHandler: ['tl', 'tm', 'tr', 'r', 'br', 'bm', 'l', 'bl'],
        extra: item,
        grid: [1, 1],
        axis: 'xy'
      }))
    },

    /**
     * 新增元件到編輯區域
     * @param {Object} param 包含components和parentId
     * @param {Array} param.components 要新增的元件陣列
     * @param {string} [param.parentId] 父元件ID
     */
    addControl({ components, parentId }) {
      let controls = []
      const newComponents = this.getComponents(components, parentId)
      if (parentId) {
        const { path } = findComponentPathById(this.controls, parentId)
        controls = updateTreeIn(this.controls, path, (item) => {
          item.children = item.children.concat(newComponents)
          return item
        })
      } else {
        controls = this.controls.concat(newComponents)
      }

      this.setControls(controls)

      const { component } = findComponentPathById(controls, newComponents[newComponents.length - 1].id)
      this.handleSelect(component)
    },

    /**
     * 批量更新元件屬性
     * @param {Object} changes 元件ID為key,要更新的屬性和值為value的物件
     */
    batchUpdateControlValue(changes) {
      const controls = batchUpdateIn(this.controls, Object.keys(changes), (item) => {
        item[changes[item.id].key] = changes[item.id].value
        return item
      })
      this.setControls(controls)
    },

    /**
     * 批量刪除指定ID的元件
     * @param {Array<string>} deleteIds 要刪除的元件ID陣列
     */
    batchDeleteControls(deleteIds) {
      const controls = batchUpdateIn(this.controls, deleteIds, () => false)
      this.setControls(controls)
    },

    /**
     * 更新選中元件的屬性
     * @param {string} key 屬性名稱
     * @param {any} value 屬性值
     * @param {boolean} isExtra 是否為extra屬性
     */
    updateControlValue(key, value, isExtra) {
      const controls = updateTreeIn(this.controls, this.currentPath, (item) => {
        if (['x', 'y', 'width', 'height', 'rotation'].includes(key)) {
          const transform = { ...item.transform }
          transform[key] = value
          item.transform = transform
          return item
        } else if (isExtra) {
          const extra = { ...item.extra }
          extra[key] = value
          item.extra = extra
        } else {
          item[key] = value
        }
        return item
      })
      this.setControls(controls)
    },

    /**
     * 處理元件變形(拖曳/調整大小/旋轉)
     * @param {Object} param transform和事件類型
     */
    handleTransform({ transform, type }) {
      this.controlled = { ...this.controlled, ...transform }
      if (['resizeend', 'dragend', 'rotateend'].includes(type)) {
        this.updateControlValue('transform', transform, false)
      }
    },

    /**
     * 更新元件的選中狀態
     * @param {boolean} status 選中狀態
     */
    updateControlStatus(status) {
      const controls = updateTreeIn(this.controls, this.currentPath, (item) => {
        item.active = status
        return item
      })
      this.setControls(controls)
    },

    /**
     * 處理元件選中
     * @param {Object} control 選中的元件
     */
    handleSelect(control) {
      this.setCurrentControl(control)
      this.updateControlStatus(true)
      if (control.extra.inputType === 'input' && control.extra.fieldname === undefined) {
        this.eventbus.$emit('dialog:showFieldName')
      }
    },

    /**
     * 設定當前選中的元件
     * @param {Object} control 要設為當前的元件
     */
    setCurrentControl(control) {
      if (!control || !control.id) {
        this.clearCurrentComponent()
        return
      }

      if (control && this.currentId) {
        const controls = updateTreeIn(this.controls, this.currentPath, (item) => {
          item.active = false
          return item
        })
        this.setControls(controls, false)
      }

      control = JSON.parse(JSON.stringify(control))
      Object.assign(control, control.transform, { active: true })
      const { path } = findComponentPathById(this.controls, control.id)
      this.currentPath = path
      this.controlled = control
      this.currentId = control.id
    },

    /**
     * 處理元件屬性變更
     * @param {Object} param 變更的屬性資訊
     */
    handleChange({ name, value, extra }) {
      if (!this.currentId) {
        return
      }
      if (extra) {
        this.controlled.extra[name] = value
      } else {
        this.controlled[name] = value
      }
      this.updateControlValue(name, value, extra)
    },

    /**
     * 取得選中的元件
     * @param {Array} ctls 元件陣列
     * @returns {Object} 選中的元件
     */
    getActiveComponent(ctls) {
      return findComponent(ctls, (item) => item.active)
    },

    /**
     * 設定元件並更新歷史記錄
     * @param {Array} controls 元件陣列
     * @param {boolean} needRecordHistory 是否記錄歷史
     */
    setControls(controls, needRecordHistory = true) {
      this.controls = controls
      if (needRecordHistory) {
        historyStack = historyStack.slice(0, historyPointer + 1)
        historyStack.push(this.controls)
        historyPointer = historyStack.length - 1
      }
    },

    /**
     * 清除當前選中的元件
     */
    clearCurrentComponent() {
      this.controlled = {}
      this.currentId = ''
      this.currentPath = []
    },

    /**
     * 從歷史記錄載入
     * @param {number} bounce 歷史位移量
     */
    initFromHistory(bounce) {
      historyPointer += bounce
      this.controls = historyStack[historyPointer]
    },

    /**
     * 處理復原操作
     */
    handleUndo() {
      if (historyPointer === 0) return
      this.clearCurrentComponent()
      this.initFromHistory(-1)
      this.setCurrentControl(this.getActiveComponent(this.controls))
    },

    /**
     * 刪除當前選中的元件
     */
    deleteComponent() {
      if (!this.currentId) {
        return
      }
      const controls = updateTreeIn(this.controls, this.currentPath, () => false)
      this.setControls(controls)
      this.clearCurrentComponent()
    },

    /**
     * 複製當前選中的元件
     */
    duplicateComponent() {
      if (!this.currentId) {
        return
      }

      const pathes = this.currentPath.slice()
      const selectedIndex = pathes.pop()

      let controls = []
      let component = null
      const newComponent = (item) => {
        const copyOfSelected = deepCopyComponent(item)
        const t = copyOfSelected.transform
        copyOfSelected.transform = {
          x: t.x,
          y: t.y + t.height,
          width: t.width,
          height: t.height,
          rotation: t.rotation
        }
        component = copyOfSelected
        return copyOfSelected
      }

      if (pathes.length > 0) {
        controls = updateTreeIn(this.controls, pathes, (item) => {
          const children = item.children.slice()
          const copyOfSelected = newComponent(children[selectedIndex])
          children.splice(selectedIndex + 1, 0, copyOfSelected)
          item.children = children
          return item
        })
      } else {
        controls = this.controls.slice()
        controls.splice(selectedIndex + 1, 0, newComponent(this.controls[selectedIndex]))
      }
      this.setControls(controls)
      this.setCurrentControl(component)
    },

    /**
     * 清除所有元件
     */
    handleClear() {
      this.setControls([])
      this.clearCurrentComponent()
    },

    /**
     * 處理重做操作
     */
    handleRedo() {
      if (historyPointer === historyStack.length - 1) return
      this.initFromHistory(1)
      this.clearCurrentComponent()
      this.setCurrentControl(this.getActiveComponent(this.controls))
    },

    /**
     * 取消選取當前元件
     */
    handleUnselect() {
      if (
        this.controlled.extra &&
        this.controlled.extra.inputType &&
        this.controlled.extra.inputType === 'input' &&
        document.getElementById(this.controlled.id) !== null
      ) {
        document.getElementById(this.controlled.id).blur()
      }
      if (!this.currentId) return
      this.updateControlStatus(false)
      this.clearCurrentComponent()
    },

    /**
     * 取得編輯器實例
     */
    getEditorView() {
      return this.$refs.editor
    },

    /**
     * 更新radio類型元件的值
     * @param {Array} children 子元件陣列
     */
    updateRadioValues(children) {
      for (let i = 0; i < children.length; i++) {
        if (children[i].extra.inputType === 'radio') {
          if (children[i].extra.disabled === false) {
            children[i].extra.value = document.getElementById(children[i].id).checked
          } else children[i].extra.value = false
        }
        if (children[i].children) {
          this.updateRadioValues(children[i].children)
        }
      }
    },

    /**
     * 儲存資料
     */
    handleSave(_docTreeData, _docData) {
      this.handleUnselect()
      this.updateRadioValues(this.controls)
      this.eventbus.$emit('saveData', this.controls)
    },

    /**
     * 更新資料
     */
    handleUpdate() {
      this.handleUnselect()
      this.updateRadioValues(this.controls)
      this.eventbus.$emit('updateData', this.controls)
    },

    /**
     * 設定欄位名稱
     * @param {string} fieldname 欄位名稱
     */
    setFieldName(fieldname) {
      const controls = updateTreeIn(this.controls, this.currentPath, (item) => {
        item.extra.fieldname = fieldname
        return item
      })
      this.setControls(controls)
    },

    /**
     * 建立簽名資料
     * @param {any} toData 目標資料
     * @param {any} data 簽名資料
     */
    createSignedData(toData, data) {
      const controls = updateTreeIn(this.controls, this.currentPath, (item) => {
        item.extra.dataPoint = toData
        item.extra.signedPhoto = data
        return item
      })
      this.setControls(controls)
    }
  },
  render() {
    return (
      <div class="vs-editor-app">
        <div class="content">
          <EditorViewVue ref="editor" value={this.controls}>
            <PluginSelectionVue application={this} />
            <PluginGridVue />
          </EditorViewVue>
        </div>
        <FooterVue />
      </div>
    )
  }
}
</script>

<style lang="less">
.vs-editor-app {
  display: flex;
  flex-direction: column;
  height: 100vh;

  .content {
    display: flex;
    flex: 1;
    height: 0;

    .vs-editor {
      flex: 1;
      position: relative;
      width: 100%;
      // overflow: scroll;
      height: 100%;
    }
  }

  .component-impl,
  .match-parent {
    width: 100%;
    height: 100%;
    display: block;
  }

  .input-has-label {
    display: flex;
    align-items: center;
  }

  // 自定义class样式
  .yoyoo-ddr.ddr-dragging {
    &.active {
      border: 1px solid blue;
      border-radius: 2px;
    }

    .resize-handler-wrapper,
    .rotate-handler {
      display: none;
    }
  }

  .yoyoo-ddr.ddr-ready-dragging {
    &.active {
      border: 1px solid #ff0000;
    }

    .resize-handler-wrapper,
    .rotate-handler {
      display: none;
    }
  }
}
</style>
